import React, { useState, useEffect } from 'react';
import { X, BarChart3, PieChart, TrendingUp, Activity, Zap, LineChart, LayoutGrid, Loader2 } from 'lucide-react';
import { api } from '../utils/apiClient';

const FUENTES = [
  { id: 'casos_activos', label: 'Casos Activos', desc: 'Total de casos en proceso', charts: ['kpi'] },
  { id: 'casos_totales', label: 'Casos Totales', desc: 'Historico acumulado', charts: ['kpi'] },
  { id: 'asistencias_totales', label: 'Asistencias Totales', desc: 'Interacciones registradas', charts: ['kpi'] },
  { id: 'asistencias_por_tipo', label: 'Por Tipo de Servicio', desc: 'Telefonico, Sitio, Conciliacion...', charts: ['bar', 'pie', 'comparative'] },
  { id: 'asistencias_por_hora', label: 'Por Hora del Dia', desc: 'Distribucion horaria de actividad', charts: ['bar', 'heatmap'] },
  { id: 'flujo_mensual', label: 'Flujo Mensual', desc: 'Tendencia de casos en el tiempo', charts: ['area', 'line', 'comparative'] },
  { id: 'casos_por_cierre', label: 'Por Cierre de Servicio', desc: 'Desglose por tipo de cierre', charts: ['bar', 'pie', 'comparative'] },
  { id: 'casos_por_responsabilidad', label: 'Por Responsabilidad', desc: 'Asegurado, Tercero, etc.', charts: ['bar', 'pie', 'comparative'] },
];

const CHART_TYPES = {
  kpi: { label: 'Tarjeta KPI', icon: TrendingUp, desc: 'Numero destacado' },
  area: { label: 'Grafico de Area', icon: Activity, desc: 'Tendencia con relleno' },
  bar: { label: 'Grafico de Barras', icon: BarChart3, desc: 'Comparativa visual' },
  pie: { label: 'Pastel / Dona', icon: PieChart, desc: 'Distribucion porcentual' },
  line: { label: 'Grafico de Linea', icon: LineChart, desc: 'Tendencia limpia' },
  heatmap: { label: 'Mapa de Calor', icon: LayoutGrid, desc: 'Actividad por hora' },
  comparative: { label: 'Barras Verticales', icon: Zap, desc: 'Comparativa vertical' },
};

const INITIAL_STATE = {
  fuente: '', tipoGrafico: '', titulo: '',
  filtros: { cliente_corporativo: '', abogado: '', tarifa: '', fecha: '', cierre_servicio: '' },
};

export default function AddWidgetModal({ isOpen, onClose, onAdd, editWidget }) {
  const [form, setForm] = useState(INITIAL_STATE);
  const [step, setStep] = useState(1);

  // Listas desplegables
  const [clientes, setClientes] = useState([]);
  const [abogados, setAbogados] = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [cierresServicio, setCierresServicio] = useState([]);
  const [anios, setAnios] = useState([]);
  const [loadingOpciones, setLoadingOpciones] = useState(false);

  // Cargar opciones al abrir el modal
  useEffect(() => {
    if (!isOpen) return;
    const fetchOpciones = async () => {
      setLoadingOpciones(true);
      try {
        const [casosData, cierresData, tarifasData, aniosData] = await Promise.all([
          api.get('/casos').catch(() => []),
          api.get('/desplegables/cierre_servicio').catch(() => []),
          api.get('/desplegables/tarifas_servicio').catch(() => []),
          api.get('/dashboard/anios_disponibles').catch(() => []),
        ]);

        const allAbogados = [...new Set((casosData || []).map(c => c.abogado).filter(Boolean))].sort();
        // Multiasistencia es parte de ASSISPREX — filtrar del dropdown
        const EXCLUDE_CLIENTS = ['MULTIASISTENCIA', 'MULTIASISTENCIAHOGAR'];
        const allClientes = [...new Set((casosData || []).map(c => c.cliente_corporativo).filter(Boolean))]
          .filter(c => !EXCLUDE_CLIENTS.includes(c.toUpperCase().replace(/\s+/g, '')))
          .sort();

        setAbogados(allAbogados);
        setClientes(allClientes);
        setTiposServicio((tarifasData || []).filter(Boolean).filter(t => t !== 'OTROS').sort());
        setCierresServicio((cierresData || []).filter(Boolean).sort());
        setAnios(Array.isArray(aniosData) ? aniosData : []);
      } catch (e) {
        console.error('Error cargando opciones:', e);
      } finally {
        setLoadingOpciones(false);
      }
    };
    fetchOpciones();
  }, [isOpen]);

  useEffect(() => {
    if (editWidget) {
      setForm({
        fuente: editWidget.fuente, tipoGrafico: editWidget.tipoGrafico,
        titulo: editWidget.titulo, filtros: { ...INITIAL_STATE.filtros, ...editWidget.filtros },
      });
      setStep(3);
    } else {
      setForm(INITIAL_STATE);
      setStep(1);
    }
  }, [editWidget, isOpen]);

  if (!isOpen) return null;

  const selectedFuente = FUENTES.find(f => f.id === form.fuente);
  const availableCharts = selectedFuente ? selectedFuente.charts : [];

  const handleFuenteSelect = (id) => {
    const f = FUENTES.find(x => x.id === id);
    setForm(prev => ({
      ...prev, fuente: id,
      tipoGrafico: f && f.charts.length === 1 ? f.charts[0] : '',
      titulo: prev.titulo || (f ? f.label : ''),
    }));
    if (f && f.charts.length === 1) setStep(3);
    else setStep(2);
  };

  const handleChartSelect = (type) => {
    setForm(prev => ({ ...prev, tipoGrafico: type }));
    setStep(3);
  };

  const handleSubmit = () => {
    if (!form.fuente || !form.tipoGrafico || !form.titulo) return;
    const cleanFiltros = {};
    Object.entries(form.filtros).forEach(([k, v]) => { if (v) cleanFiltros[k] = v; });
    onAdd({
      id: editWidget?.id || `w${Date.now()}`,
      fuente: form.fuente, tipoGrafico: form.tipoGrafico,
      titulo: form.titulo, filtros: cleanFiltros,
    });
    setForm(INITIAL_STATE);
    setStep(1);
    onClose();
  };

  const selectClass = "w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all appearance-none";
  const inputClass = selectClass;
  const labelClass = "block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5";

  // Generar opciones de meses
  const meses = [
    { v: '01', l: 'Enero' }, { v: '02', l: 'Febrero' }, { v: '03', l: 'Marzo' },
    { v: '04', l: 'Abril' }, { v: '05', l: 'Mayo' }, { v: '06', l: 'Junio' },
    { v: '07', l: 'Julio' }, { v: '08', l: 'Agosto' }, { v: '09', l: 'Septiembre' },
    { v: '10', l: 'Octubre' }, { v: '11', l: 'Noviembre' }, { v: '12', l: 'Diciembre' },
  ];

  // Parsear fecha actual del form para los selects de año/mes
  const fechaParts = (form.filtros.fecha || '').split('-');
  const selectedAnio = fechaParts[0] || '';
  const selectedMes = fechaParts[1] || '';

  const handleFechaChange = (anio, mes) => {
    let fecha = '';
    if (anio && mes) fecha = `${anio}-${mes}`;
    else if (anio) fecha = anio;
    setForm(p => ({ ...p, filtros: { ...p.filtros, fecha } }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{editWidget ? 'Editar Widget' : 'Agregar Widget'}</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {step === 1 && 'Paso 1: Selecciona la fuente de datos'}
              {step === 2 && 'Paso 2: Elige el tipo de grafico'}
              {step === 3 && 'Paso 3: Configura titulo y filtros'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800">
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          {/* Step 1: Data source */}
          {step === 1 && (
            <div className="grid grid-cols-1 gap-2">
              {FUENTES.map(f => (
                <button key={f.id} onClick={() => handleFuenteSelect(f.id)}
                  className={`w-full text-left p-3.5 rounded-xl border transition-all ${form.fuente === f.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-100 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-slate-800/50'}`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{f.label}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{f.desc}</p>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Chart type */}
          {step === 2 && (
            <div>
              <button onClick={() => setStep(1)} className="text-xs text-blue-500 hover:text-blue-700 mb-3 flex items-center gap-1">
                ← Cambiar fuente: <span className="font-semibold">{selectedFuente?.label}</span>
              </button>
              <div className="grid grid-cols-2 gap-3">
                {availableCharts.map(c => {
                  const ct = CHART_TYPES[c];
                  const Icon = ct.icon;
                  return (
                    <button key={c} onClick={() => handleChartSelect(c)}
                      className={`p-4 rounded-xl border text-center transition-all ${form.tipoGrafico === c
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-100 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-slate-800/50'}`}>
                      <Icon size={24} className="mx-auto mb-2 text-blue-500" />
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{ct.label}</p>
                      <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-0.5">{ct.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Title + filters */}
          {step === 3 && (
            <div className="space-y-4">
              {!editWidget && (
                <button onClick={() => setStep(availableCharts.length > 1 ? 2 : 1)} className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1">
                  ← {selectedFuente?.label} / {CHART_TYPES[form.tipoGrafico]?.label}
                </button>
              )}

              <div>
                <label className={labelClass}>Titulo del Widget</label>
                <input type="text" value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} className={inputClass} placeholder="Ej: Servicios del mes" autoFocus />
              </div>

              <div className="border-t border-gray-100 dark:border-slate-800 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-3">
                  Filtros opcionales
                  {loadingOpciones && <Loader2 size={12} className="inline-block ml-2 animate-spin" />}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Aseguradora / Cliente */}
                  <div>
                    <label className={labelClass}>Aseguradora</label>
                    <select value={form.filtros.cliente_corporativo} onChange={e => setForm(p => ({ ...p, filtros: { ...p.filtros, cliente_corporativo: e.target.value } }))} className={selectClass}>
                      <option value="">Todas</option>
                      {clientes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Abogado */}
                  <div>
                    <label className={labelClass}>Abogado</label>
                    <select value={form.filtros.abogado} onChange={e => setForm(p => ({ ...p, filtros: { ...p.filtros, abogado: e.target.value } }))} className={selectClass}>
                      <option value="">Todos</option>
                      {abogados.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>

                  {/* Tipo de Servicio */}
                  <div>
                    <label className={labelClass}>Tipo Servicio</label>
                    <select value={form.filtros.tarifa} onChange={e => setForm(p => ({ ...p, filtros: { ...p.filtros, tarifa: e.target.value } }))} className={selectClass}>
                      <option value="">Todos</option>
                      {tiposServicio.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  {/* Cierre de Servicio */}
                  <div>
                    <label className={labelClass}>Cierre Servicio</label>
                    <select value={form.filtros.cierre_servicio} onChange={e => setForm(p => ({ ...p, filtros: { ...p.filtros, cierre_servicio: e.target.value } }))} className={selectClass}>
                      <option value="">Todos</option>
                      {cierresServicio.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Ano */}
                  <div>
                    <label className={labelClass}>Ano</label>
                    <select value={selectedAnio} onChange={e => handleFechaChange(e.target.value, selectedMes)} className={selectClass}>
                      <option value="">Todos</option>
                      {anios.map(a => <option key={a} value={String(a)}>{a}</option>)}
                    </select>
                  </div>

                  {/* Mes */}
                  <div>
                    <label className={labelClass}>Mes</label>
                    <select value={selectedMes} onChange={e => handleFechaChange(selectedAnio, e.target.value)} className={selectClass} disabled={!selectedAnio}>
                      <option value="">Todos</option>
                      {meses.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 3 && (
          <div className="flex justify-end gap-3 p-5 border-t border-gray-100 dark:border-slate-800">
            <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white transition-colors rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800">
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={!form.fuente || !form.tipoGrafico || !form.titulo}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-semibold rounded-xl shadow transition-all active:scale-95">
              {editWidget ? 'Guardar Cambios' : 'Agregar Widget'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
